import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ChangelogResource, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';

import StrategyFormModal from './StrategyFormModal';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  box-sizing: border-box;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  margin-right: 8px;
  > div {
    padding: 0 8px;
  }
`;

const EventCol = styled.div`
  width: 26%;
`;

const ChannelCol = styled.div`
  width: 18%;
`;

const DateCol = styled.div`
  width: 18%;
`;

const NoteCol = styled.div`
  width: 38%;
`;

const Heading = styled(Text)`
  margin-bottom: 8px;
`;

const Channel = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 5px;
  &:last-of-type {
    margin: 0;
  }
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid rgba(144, 144, 144, 0.3);
`;

interface Props {
  changelog: ChangelogResource;
  queryKey: Array<any>;
}

const StrategyChangelogCard: FC<Props> = ({ changelog, queryKey }) => {
  const [showEdit, setShowEdit] = useState(false);

  const alerts = useAlerts();

  const { teamId } = useParams();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to delete this event?`,
        changelog.name,
      );

      if (!confirmed) return;

      const { id } = await api.delete<never, { id: number }>(
        `/v1/changelogs/${changelog.id}`,
      );

      alerts.success('Changelog was successfully deleted');

      const queryData =
        queryClient.getQueryData<
          InfiniteData<PaginatedResponse<ChangelogResource>>
        >(queryKey)!;

      const newQueryData = {
        ...queryData,
        pages: queryData.pages.map((page) => ({
          ...page,
          data: page.data.filter((event) => event.id !== id),
        })),
      };

      queryClient.setQueryData(queryKey, newQueryData);
    } catch (err) {
      alerts.error();
    }
  };
  return (
    <Card>
      <Content>
        <EventCol>
          <Heading $color="gray" $type="smallBold">
            Event
          </Heading>
          <Text $type="smallBold">{changelog.name}</Text>
        </EventCol>
        <ChannelCol>
          <Heading $color="gray" $type="smallBold">
            Channel
          </Heading>
          {!!changelog.channels?.length ? (
            changelog.channels.map((channel) => (
              <Channel key={channel.id}>
                {!!channel.thumbnail && (
                  <Image
                    src={channel.thumbnail}
                    alt={channel.name + ' thumbnail'}
                    loading="lazy"
                  />
                )}
                <Text $type="smallMedium">{channel.name}</Text>
              </Channel>
            ))
          ) : (
            <Text $type="smallMedium">-</Text>
          )}
        </ChannelCol>
        <DateCol>
          <Heading $color="gray" $type="smallBold">
            Date & Time
          </Heading>
          <Text $type="smallMedium">
            {DateTime.fromISO(changelog.eventTime).toFormat('dd/MM/yyyy · T')}
          </Text>
        </DateCol>
        <NoteCol>
          <Heading $color="gray" $type="smallBold">
            Note
          </Heading>
          <Text $type="smallMedium">{changelog.notes || '-'}</Text>
        </NoteCol>
      </Content>

      <ActionsWrap>
        <ActionsDropdown>
          {teamId !== 'all' && (
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowEdit(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
          )}
          <ActionsDropdownButton as="button" onClick={onDelete}>
            <span>
              <TrashIcon />
            </span>
            Delete
          </ActionsDropdownButton>
        </ActionsDropdown>
        <AnimatePresence>
          {showEdit && (
            <StrategyFormModal
              changelog={changelog}
              onClose={() => setShowEdit(false)}
              queryKey={queryKey}
            />
          )}
        </AnimatePresence>
      </ActionsWrap>
    </Card>
  );
};

export default StrategyChangelogCard;
